// @flow
import {Unveil} from '@homeaway/react-unveil';
import gql from 'graphql-tag';
import {debounce} from 'lodash';
import React, {Fragment} from 'react';
import classnames from 'classnames';
import HitPlaceholder from './HitPlaceholder';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import HitInfo, {fragments as hitInfoFragments} from '../HitInfo/HitInfo';
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import {PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
import {VirtualTourBadge} from '../VirtualTourBadge/VirtualTourBadge';
import {parseHitUnveilClass} from '../HitCollection/hitCollectionUtils';

import type {HitImage} from '../HitCarouselContainer/HitCarouselContainer';
import type {
    SERPHitShape as HitInfoSERPHitShape,
    ListingShape as HitInfoListingShape
} from '../HitInfo/HitInfo';

import './Hit.less';

export const fragments = {
    SERPHit: gql`
        fragment HitSERPHit on SERPHit {
            absolutePosition
            imageCount
            images {
                lowResSrc
                src
                altText
            }
            relativePosition
            thumbnailUrl1
            ...HitInfoSERPHit
        }
        ${hitInfoFragments.SERPHit}
    `,
    listing: gql`
        fragment HitListing on Listing {
            virtualTourBadge {
                name
                id
                helpText
            }
            ...HitInfoListing
        }
        ${hitInfoFragments.listing}
    `
};

export type SERPHitShape = {
    absolutePosition: number,
    imageCount: number,
    images: HitImage[],
    relativePosition: number,
    thumbnailUrl1: string
} & HitInfoSERPHitShape;

export type BadgeShape = {
    id: string,
    name: string,
    helpText: ?string
};

export type ListingShape = HitInfoListingShape & {
    virtualTourBadge: ?BadgeShape
};

export type Props = {
    hit?: SERPHitShape,
    listing?: ListingShape,
    isWithoutAnchor?: boolean,
    index?: number,
    onHitClick?: (hit: SERPHitShape, listing: ListingShape) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (listingId: string) => void
};

type CarouselProps = {
    hit?: SERPHitShape,
    listing?: ListingShape,
    onClick: (Hit: SERPHitShape, listing: ListingShape) => ?void
};

export function Carousel({hit, listing, onClick}: CarouselProps) {
    if (!hit || !listing) {
        return null;
    }

    return (
        <span aria-hidden="true">
            {listing.virtualTourBadge && (
                <VirtualTourBadge
                    badge={listing.virtualTourBadge}
                    detailPageUrl={listing.detailPageUrl}
                    onBadgeClick={onClick}
                />
            )}
            <div className="Hit__tripboardButton">
                <TripBoardButton hit={hit} listing={listing} />
            </div>
            <HitCarouselContainer
                detailPageUrl={listing.detailPageUrl}
                edapAction={PHOTOCAROUSEL_ADVANCE}
                images={hit.images}
                listingId={listing.listingId}
                totalImageCount={hit.imageCount}
                onImageClick={onClick}
            />
        </span>
    );
}

export function CarouselContainer(props: CarouselProps) {
    const hit = props.hit || {};
    const {relativePosition} = hit;
    const shouldAppendUnveilStyle = +(relativePosition || undefined) <= 3; // eslint-disable-line no-undefined

    const classes = classnames('HitCarousel', 'Hit__thumbnail', {
        [parseHitUnveilClass(relativePosition - 1)]: shouldAppendUnveilStyle
    });

    if (typeof window !== 'undefined' && window.__IE11__) {
        return (
            <div className={classes}>
                <Carousel {...props} />
            </div>
        );
    }

    return (
        <Unveil
            className={classes}
            threshold={1200}
            throttleAmount={300}
            useWindowAsScrollParent
        >
            <Carousel {...props} />
        </Unveil>
    );
}

export default function Hit(props: Props) {
    const {hit, onHitClick, onMouseEnter, onMouseLeave, index, listing} = props;
    const handleMouseEnter = () =>
        listing && onMouseEnter ? onMouseEnter(listing.listingId) : null;
    const handleMouseLeave = () =>
        listing && onMouseLeave ? onMouseLeave(listing.listingId) : null;
    // TODO: fix this click handler for listings
    const onClick = () =>
        hit && listing && onHitClick ? onHitClick(hit, listing) : null;
    const onMouseTimeout = 100;

    return (
        <div
            className="Hit media-flex media-flex--left media-flex--xs"
            data-wdio="hit"
            role="listitem"
            onMouseEnter={debounce(handleMouseEnter, onMouseTimeout)}
            onMouseLeave={debounce(handleMouseLeave, onMouseTimeout)}
        >
            {!hit || !listing ? (
                <HitPlaceholder />
            ) : (
                <Fragment>
                    <CarouselContainer
                        hit={hit}
                        listing={listing}
                        onClick={onClick}
                    />
                    <Fragment>
                        <a
                            target={'_blank'}
                            className="media-flex__content"
                            href={listing.detailPageUrl}
                            onClick={onClick}
                            tabIndex="0"
                        >
                            <HitInfo
                                hit={hit}
                                listing={listing}
                                hitIndex={index}
                            />
                        </a>
                    </Fragment>
                </Fragment>
            )}
        </div>
    );
}
